/**
 * Every user-facing string, Hindi and English. Hindi is the default; the toggle in the
 * header switches language and is remembered on the phone.
 *
 * Add a key to `hi` first; `en` must have the same keys (TypeScript checks it).
 */

const hi = {
  appName: "N-PAC बूथ",
  langToggle: "English",
  ok: "ठीक है",
  cancel: "रद्द करें",
  back: "वापस",
  save: "सेव करें",
  saving: "सेव हो रहा है…",
  retry: "फिर से कोशिश",
  discard: "हटाएं",
  remove: "हटाएं",
  yes: "हाँ",
  no: "नहीं",
  loading: "लोड हो रहा है…",
  offline: "ऑफलाइन",
  online: "ऑनलाइन",
  required: "ज़रूरी",
  optional: "वैकल्पिक",

  // login
  loginTitle: "लॉगिन करें",
  loginSubtitle: "बूथ सर्वे ऐप",
  phone: "मोबाइल नंबर",
  phoneHint: "10 अंकों का मोबाइल नंबर",
  loginId: "ईमेल या मोबाइल",
  loginIdHint: "जैसे name@npac.in या 98XXXXXXXX",
  loginInvalid: "सही ईमेल या 10 अंकों का मोबाइल नंबर लिखें",
  password: "पासवर्ड",
  showPassword: "पासवर्ड दिखाएं",
  hidePassword: "पासवर्ड छिपाएं",
  loginButton: "लॉगिन",
  loggingIn: "लॉगिन हो रहा है…",
  loginWrong: "ईमेल / मोबाइल या पासवर्ड गलत है",
  loginTooMany: "बहुत बार गलत कोशिश। 15 मिनट बाद फिर कोशिश करें।",
  loginOffline: "इंटरनेट नहीं है। पहली बार लॉगिन के लिए नेटवर्क चाहिए।",
  loginInactive: "यह लॉगिन बंद है। अपने एडमिन से बात करें।",
  sessionExpired: "सेशन खत्म हो गया। फिर से लॉगिन करें। आपका भरा हुआ डेटा फोन में सुरक्षित है।",
  unsentOnPhone: (n: number) => `${n} बदलाव फोन में सेव हैं, लॉगिन के बाद भेजे जाएंगे`,
  serverLabel: "सर्वर",
  logout: "लॉगआउट",
  logoutConfirm: "लॉगआउट करें? फोन में बाकी डेटा लॉगिन के बाद भेजा जाएगा।",

  // assembly
  chooseAssembly: "विधानसभा चुनें",
  downloadingBooths: "बूथ सूची डाउनलोड हो रही है…",
  boothsDownloaded: (n: number) => `${n} बूथ डाउनलोड हुए`,
  downloadFailed: "बूथ सूची डाउनलोड नहीं हुई। नेटवर्क मिलने पर फिर कोशिश करें।",
  noBoothsCached: "फोन में बूथ सूची नहीं है। नेटवर्क चालू करके रीफ्रेश करें।",
  refresh: "रीफ्रेश",
  updatedAt: (s: string) => `अपडेट: ${s}`,

  // booth picker
  boothNo: "बूथ न०",
  boothNoHint: "बूथ नंबर लिखें, जैसे 45 या 45A",
  boothNotFound: "इस नंबर का बूथ नहीं मिला",
  open: "खोलें",
  name: "नाम",
  mandal: "मंडल",
  panchayat: "पंचायत",
  village: "गांव / मोहल्ला",
  landmark: "पहचान का स्थान (लैंडमार्क)",
  allBooths: "सभी बूथ",
  myBooths: "मेरे बूथ",
  notAssigned: "आपको नहीं दिया गया (सिर्फ देख सकते हैं)",
  viewOnly: "सिर्फ देखें",
  newBooth: "नया",
  boothCount: (n: number) => `${n} बूथ`,
  locationShared: "लोकेशन भेजी गई",

  // booth screen
  sections: "भाग",
  shareLocation: "लोकेशन शेयर करें",
  shareLocationSub: "बूथ पर खड़े होकर दबाएं",
  locationOnServer: (acc: string, when: string) => `लोकेशन सेव है (±${acc} मी, ${when})`,
  locationWaiting: "लोकेशन भेजनी बाकी है",
  locationFailed: "लोकेशन नहीं भेजी जा सकी",
  lastBy: (who: string) => `पिछला बदलाव: ${who}`,
  draftSaved: "अधूरा ड्राफ्ट फोन में है",

  // section names
  sec_identity: "1. पहचान",
  sec_agents: "2. एजेंट",
  sec_karyakarta: "3. 20 सक्रिय सदस्य",
  sec_demographics: "4. जनसांख्यिकी व जाति",
  sec_ground: "5. जमीनी जानकारी",

  // statuses
  st_complete: "पूरा",
  st_partial: "अधूरा",
  st_mismatch: "योग नहीं मिलता",
  st_not_started: "शुरू नहीं",
  st_waiting: "भेजना बाकी",
  st_conflict: "टकराव",
  st_failed: "नहीं भेजा गया",

  // forms common
  formSavedSynced: "सेव हो गया और सर्वर पर भेज दिया",
  formSavedQueued: "फोन में सेव हो गया। नेटवर्क मिलते ही भेज देंगे।",
  formHasErrors: "कुछ जानकारी सही नहीं है, लाल निशान देखें",
  formMismatchSaved: "सेव हुआ, लेकिन योग नहीं मिलता। बाद में सुधार सकते हैं।",
  draftRestored: "पिछली बार का अधूरा फॉर्म वापस लाया गया",
  discardDraft: "ड्राफ्ट हटाएं",
  discardDraftConfirm: "इस फॉर्म के बदलाव हटा दें?",
  unsavedLeave: "बदलाव ड्राफ्ट में सेव रहेंगे। बाद में पूरा कर सकते हैं।",
  notAllowedEdit: "यह बूथ आपको नहीं दिया गया है। आप सिर्फ देख सकते हैं।",
  pendingConflictBanner: "इस भाग में टकराव है। सिंक स्क्रीन पर चुनें कौन सा डेटा रखना है।",
  serverRejected: "सर्वर ने यह डेटा नहीं लिया:",

  // identity
  identityHelp: "मंडल, पंचायत या गांव में से कम से कम एक भरें।",

  // agents
  pollingAgent: "पोलिंग एजेंट",
  coPollingAgent: "सह-पोलिंग एजेंट",
  bla2: "BLA-2 (बूथ लेवल एजेंट, पार्टी)",
  blo: "BLO (बूथ लेवल ऑफ़िसर, चुनाव कार्यालय)",
  agentName: "नाम",
  agentPhone: "मोबाइल (10 अंक)",
  clearPerson: "खाली करें",

  // karyakarta
  workersCount: (n: number) => `${n}/20 कार्यकर्ता`,
  addWorker: "+ कार्यकर्ता जोड़ें",
  maxWorkers: "20 कार्यकर्ता पूरे",
  slot: "क्रमांक",
  mobile: "मोबाइल",
  whatsapp: "व्हाट्सऐप",
  sameAsMobile: "मोबाइल वाला ही",
  role: "भूमिका / पद",
  caste: "जाति",
  influence: "प्रभाव",
  inf_high: "ज़्यादा",
  inf_medium: "मध्यम",
  inf_normal: "सामान्य",
  removeWorkerConfirm: (slot: number) => `क्रमांक ${slot} का कार्यकर्ता हटाएं?`,
  noWorkers: "अभी कोई कार्यकर्ता नहीं जोड़ा गया",

  // demographics
  totalVoters: "कुल मतदाता",
  ageGroups: "आयु वर्ग",
  age18_25: "18–25",
  age26_40: "26–40",
  age41_60: "41–60",
  age60plus: "60+",
  ageSum: "आयु वर्ग का योग",
  casteTable: "जाति तालिका",
  casteName: "जाति का नाम",
  male: "पुरुष",
  female: "महिला",
  core: "कोर जाति",
  addCaste: "+ जाति जोड़ें",
  otherGender: "अन्य (थर्ड जेंडर)",
  genderTotals: "लिंग का योग (जाति तालिका से)",
  casteSum: "जाति तालिका का योग",
  totalLabel: "कुल",
  matches: "मिलता है",
  mismatchWarn: (sum: number, total: number) => `योग ${sum} है, कुल मतदाता ${total}। फिर भी सेव कर सकते हैं।`,
  enterTotalFirst: "पहले कुल मतदाता भरें",

  // ground
  problems: "बूथ की मुख्य समस्याएं",
  suggestions: "वोट बढ़ाने के सुझाव",
  problemN: (n: number) => `समस्या ${n}`,
  suggestionN: (n: number) => `सुझाव ${n}`,

  // location
  locTitle: "लोकेशन शेयर करें",
  locIntro: "बूथ के गेट के पास खुले में खड़े हों। GPS कुछ सेकंड में सटीक होता है।",
  locPermissionDenied: "लोकेशन की अनुमति नहीं मिली। सेटिंग में जाकर अनुमति दें।",
  openSettings: "सेटिंग खोलें",
  locServicesOff: "फोन का GPS / लोकेशन बंद है। चालू करें।",
  locSearching: "GPS ढूंढ रहा है…",
  locAccuracy: "सटीकता",
  metres: "मी",
  locGood: "अच्छी सटीकता",
  locWeak: (m: number) => `सटीकता ${m} मी से कम नहीं है। खुले में जाएं या थोड़ा रुकें। फिर भी भेज सकते हैं।`,
  locSend: "यह लोकेशन भेजें",
  locSendAnyway: "फिर भी भेजें",
  locTryAgain: "फिर से ढूंढें",
  locSentSynced: "लोकेशन सर्वर पर भेज दी गई",
  locSentQueued: "लोकेशन फोन में सेव है, नेटवर्क मिलते ही भेजेंगे",
  locOutsideIndia: "यह लोकेशन भारत के बाहर है। GPS फिर से लें।",
  locNoFix: "अभी तक GPS नहीं मिला। खुले में जाकर फिर कोशिश करें।",
  locCapturedAt: (s: string) => `समय: ${s}`,
  openInMaps: "मैप में देखें",
  locWatchingFor: (s: number) => `${s} सेकंड`,

  // sync
  syncTitle: "सिंक",
  syncNow: "अभी सिंक करें",
  syncing: "सिंक हो रहा है…",
  syncAllDone: "सब कुछ सर्वर पर भेज दिया गया है",
  syncPendingN: (n: number) => `${n} भेजना बाकी`,
  syncFailedN: (n: number) => `${n} नहीं गए`,
  syncConflictN: (n: number) => `${n} टकराव`,
  syncOfflineNote: "नेटवर्क नहीं है। नेटवर्क आते ही अपने आप भेजेंगे।",
  syncLoginNeeded: "भेजने के लिए फिर से लॉगिन करें",
  syncLastRun: (s: string) => `पिछला सिंक: ${s}`,
  syncNextRetry: (s: string) => `अगली कोशिश: ${s}`,
  kindLocation: "लोकेशन",
  attempts: (n: number) => `${n} बार कोशिश`,
  conflictExplain: "आपके भेजने से पहले किसी और ने यह भाग बदल दिया। कौन सा डेटा रखना है?",
  mine: "मेरा डेटा",
  theirs: "सर्वर का डेटा",
  keepMine: "मेरा रखें",
  useServer: "सर्वर वाला रखें",
  keepMineConfirm: "आपका डेटा सर्वर वाले डेटा को बदल देगा। ठीक है?",
  useServerConfirm: "आपके बदलाव हट जाएंगे। ठीक है?",
  discardConfirm: "यह बदलाव फोन से हटा दें? यह सर्वर पर नहीं जाएगा।",
  updatedBy: (who: string, when: string) => `${who} • ${when}`,
  status: "स्थिति",
  details: "विवरण",
  showDetails: "विवरण देखें",
  hideDetails: "विवरण छिपाएं",
  emptyValue: "—",

  // errors
  err_forbidden: "अनुमति नहीं है (यह बूथ आपको नहीं दिया गया)",
  err_not_found: "बूथ नहीं मिला",
  err_network: "नेटवर्क नहीं है",
  err_server: "सर्वर में समस्या",
  err_generic: "कुछ गलत हो गया",
};

export type Strings = typeof hi;

const en: Strings = {
  appName: "N-PAC Booth",
  langToggle: "हिंदी",
  ok: "OK",
  cancel: "Cancel",
  back: "Back",
  save: "Save",
  saving: "Saving…",
  retry: "Retry",
  discard: "Discard",
  remove: "Remove",
  yes: "Yes",
  no: "No",
  loading: "Loading…",
  offline: "Offline",
  online: "Online",
  required: "Required",
  optional: "Optional",

  loginTitle: "Log in",
  loginSubtitle: "Booth survey app",
  phone: "Mobile number",
  phoneHint: "10-digit mobile number",
  loginId: "Email or mobile",
  loginIdHint: "e.g. name@npac.in or 98XXXXXXXX",
  loginInvalid: "Enter a valid email or a 10-digit mobile number",
  password: "Password",
  showPassword: "Show password",
  hidePassword: "Hide password",
  loginButton: "Log in",
  loggingIn: "Logging in…",
  loginWrong: "Wrong email / mobile or password",
  loginTooMany: "Too many failed attempts. Try again in 15 minutes.",
  loginOffline: "No internet. The first login needs a network connection.",
  loginInactive: "This login is disabled. Contact your admin.",
  sessionExpired: "Your session ended. Please log in again. Your entries are safe on this phone.",
  unsentOnPhone: (n) => `${n} change(s) saved on this phone will be sent after login`,
  serverLabel: "Server",
  logout: "Log out",
  logoutConfirm: "Log out? Unsent data stays on the phone and is sent after the next login.",

  chooseAssembly: "Choose assembly",
  downloadingBooths: "Downloading booth list…",
  boothsDownloaded: (n) => `${n} booths downloaded`,
  downloadFailed: "Could not download the booth list. Try again when you have network.",
  noBoothsCached: "No booth list on this phone yet. Turn on the network and refresh.",
  refresh: "Refresh",
  updatedAt: (s) => `Updated: ${s}`,

  boothNo: "Booth no.",
  boothNoHint: "Type the booth number, e.g. 45 or 45A",
  boothNotFound: "No booth with this number",
  open: "Open",
  name: "Name",
  mandal: "Mandal",
  panchayat: "Panchayat",
  village: "Village / mohalla",
  landmark: "Landmark",
  allBooths: "All booths",
  myBooths: "My booths",
  notAssigned: "Not assigned to you (view only)",
  viewOnly: "View only",
  newBooth: "New",
  boothCount: (n) => `${n} booths`,
  locationShared: "Location shared",

  sections: "Sections",
  shareLocation: "Share location",
  shareLocationSub: "Press while standing at the booth",
  locationOnServer: (acc, when) => `Location saved (±${acc} m, ${when})`,
  locationWaiting: "Location waiting to be sent",
  locationFailed: "Location could not be sent",
  lastBy: (who) => `Last change: ${who}`,
  draftSaved: "Unfinished draft on this phone",

  sec_identity: "1. Identity",
  sec_agents: "2. Agents",
  sec_karyakarta: "3. 20 active members",
  sec_demographics: "4. Demographics & caste",
  sec_ground: "5. Ground data",

  st_complete: "Complete",
  st_partial: "Partial",
  st_mismatch: "Totals mismatch",
  st_not_started: "Not started",
  st_waiting: "Waiting to sync",
  st_conflict: "Conflict",
  st_failed: "Not sent",

  formSavedSynced: "Saved and sent to the server",
  formSavedQueued: "Saved on the phone. Will be sent when there is network.",
  formHasErrors: "Some entries are not valid; see the red marks",
  formMismatchSaved: "Saved, but the totals do not match. You can fix it later.",
  draftRestored: "Restored your unfinished form from last time",
  discardDraft: "Discard draft",
  discardDraftConfirm: "Discard the changes in this form?",
  unsavedLeave: "Changes stay in the draft. You can finish later.",
  notAllowedEdit: "This booth is not assigned to you. You can only view it.",
  pendingConflictBanner: "This section has a conflict. Choose which data to keep on the Sync screen.",
  serverRejected: "The server did not accept this data:",

  identityHelp: "Fill at least one of mandal, panchayat or village.",

  pollingAgent: "Polling agent",
  coPollingAgent: "Co-polling agent",
  bla2: "BLA-2 (Booth Level Agent, party)",
  blo: "BLO (Booth Level Officer, election office)",
  agentName: "Name",
  agentPhone: "Mobile (10 digits)",
  clearPerson: "Clear",

  workersCount: (n) => `${n}/20 workers`,
  addWorker: "+ Add worker",
  maxWorkers: "All 20 workers filled",
  slot: "Slot",
  mobile: "Mobile",
  whatsapp: "WhatsApp",
  sameAsMobile: "Same as mobile",
  role: "Role / post",
  caste: "Caste",
  influence: "Influence",
  inf_high: "High",
  inf_medium: "Medium",
  inf_normal: "Normal",
  removeWorkerConfirm: (slot) => `Remove the worker in slot ${slot}?`,
  noWorkers: "No workers added yet",

  totalVoters: "Total voters",
  ageGroups: "Age groups",
  age18_25: "18–25",
  age26_40: "26–40",
  age41_60: "41–60",
  age60plus: "60+",
  ageSum: "Age groups total",
  casteTable: "Caste table",
  casteName: "Caste name",
  male: "Male",
  female: "Female",
  core: "Core caste",
  addCaste: "+ Add caste",
  otherGender: "Other (third gender)",
  genderTotals: "Gender totals (from caste table)",
  casteSum: "Caste table total",
  totalLabel: "Total",
  matches: "matches",
  mismatchWarn: (sum, total) => `Adds up to ${sum}, total voters is ${total}. You can still save.`,
  enterTotalFirst: "Enter total voters first",

  problems: "Main problems of the booth",
  suggestions: "Ideas to raise our vote",
  problemN: (n) => `Problem ${n}`,
  suggestionN: (n) => `Suggestion ${n}`,

  locTitle: "Share location",
  locIntro: "Stand in the open near the booth gate. GPS gets more accurate over a few seconds.",
  locPermissionDenied: "Location permission was not given. Allow it in Settings.",
  openSettings: "Open settings",
  locServicesOff: "Phone GPS / location is off. Please turn it on.",
  locSearching: "Searching for GPS…",
  locAccuracy: "Accuracy",
  metres: "m",
  locGood: "Good accuracy",
  locWeak: (m) => `Accuracy is not better than ${m} m. Move into the open or wait a little. You can still send it.`,
  locSend: "Send this location",
  locSendAnyway: "Send anyway",
  locTryAgain: "Search again",
  locSentSynced: "Location sent to the server",
  locSentQueued: "Location saved on the phone; will be sent when there is network",
  locOutsideIndia: "This location is outside India. Take the GPS reading again.",
  locNoFix: "No GPS fix yet. Move into the open and try again.",
  locCapturedAt: (s) => `Time: ${s}`,
  openInMaps: "View on map",
  locWatchingFor: (s) => `${s} s`,

  syncTitle: "Sync",
  syncNow: "Sync now",
  syncing: "Syncing…",
  syncAllDone: "Everything has been sent to the server",
  syncPendingN: (n) => `${n} waiting`,
  syncFailedN: (n) => `${n} failed`,
  syncConflictN: (n) => `${n} conflict(s)`,
  syncOfflineNote: "No network. Will send automatically when the network is back.",
  syncLoginNeeded: "Log in again to send",
  syncLastRun: (s) => `Last sync: ${s}`,
  syncNextRetry: (s) => `Next try: ${s}`,
  kindLocation: "Location",
  attempts: (n) => `${n} attempt(s)`,
  conflictExplain: "Someone else changed this section before yours reached the server. Which data should be kept?",
  mine: "My data",
  theirs: "Server data",
  keepMine: "Keep mine",
  useServer: "Use server copy",
  keepMineConfirm: "Your data will replace the server's data. OK?",
  useServerConfirm: "Your changes will be dropped. OK?",
  discardConfirm: "Remove this change from the phone? It will not be sent.",
  updatedBy: (who, when) => `${who} • ${when}`,
  status: "Status",
  details: "Details",
  showDetails: "Show details",
  hideDetails: "Hide details",
  emptyValue: "—",

  err_forbidden: "Not allowed (this booth is not assigned to you)",
  err_not_found: "Booth not found",
  err_network: "No network",
  err_server: "Server problem",
  err_generic: "Something went wrong",
};

export type Lang = "hi" | "en";
export const STRINGS: Record<Lang, Strings> = { hi, en };
export const DEFAULT_LANG: Lang = "hi";

export function strings(lang: Lang): Strings {
  return STRINGS[lang] ?? hi;
}

/** Short local date-time, e.g. "23 Oct, 11:30". */
export function formatTime(iso: string | number | null | undefined, lang: Lang): string {
  if (iso == null || iso === "") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const months = lang === "hi"
    ? ["जन", "फर", "मार्च", "अप्रै", "मई", "जून", "जुला", "अग", "सित", "अक्टू", "नव", "दिस"]
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${months[d.getMonth()]}, ${hh}:${mm}`;
}
